import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { approvalRoutingService, getErrorMessage } from '../../../services/apiService';
import { LINK_TEXT, MAIL_TEXT, FILTERS, activityQuery, combosOfPerson, deliverySteps, filterItems, fmtWhen, outcomeText } from '../approverDetails';

const ROLE_TEXT = { ADMIN: 'Administrator', COORDINATOR: 'Coordinator', USER: 'User' };
const KIND = {
  INVITE: { label: 'Vendor invite', short: 'Invite', icon: 'Mail', chip: 'bg-sky-50 text-sky-800 border-sky-200' },
  QUALITY: { label: 'Quality approval', short: 'Quality', icon: 'ShieldCheck', chip: 'bg-violet-50 text-violet-800 border-violet-200' },
};
const STATUS = {
  PENDING: { label: 'Waiting', cls: 'bg-amber-50 text-amber-800 border-amber-200', icon: 'Clock' },
  APPROVED: { label: 'Approved', cls: 'bg-green-50 text-green-800 border-green-200', icon: 'CheckCircle2' },
  REJECTED: { label: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-200', icon: 'XCircle' },
};
const TONE = {
  slate: 'text-slate-600',
  sky: 'text-sky-700',
  green: 'text-green-700',
  amber: 'text-amber-700',
  red: 'text-red-700',
};

const initials = (name, email) =>
  (name || email || '?')
    .split(/\s+/)
    .map((w) => w.match(/[A-Za-z0-9]/)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Sent → Delivered → Opened → Link opened → Decided, for someone deciding by e-mail link. */
const DeliveryTrail = ({ delivery, pending }) => {
  const steps = deliverySteps(delivery);
  if (!steps.length) return null;
  const { mail, link, links_sent: sent } = delivery;
  if (steps[0].state === 'error') {
    return (
      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex gap-2" data-testid="delivery-error">
        <Icon name="MailWarning" size={14} className="flex-shrink-0 mt-px" />
        <span>
          <span className="font-semibold">E-mail {MAIL_TEXT[mail.status].label.toLowerCase()}.</span> {MAIL_TEXT[mail.status].hint}
          {mail.error ? <span className="block text-red-700/80 mt-0.5 break-words">{mail.error}</span> : null}
        </span>
      </div>
    );
  }
  const linkText = LINK_TEXT[link.state];
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5" data-testid="delivery-trail">
      <ol className="flex items-center gap-1" aria-label="What happened to the e-mail">
        {steps.map((s, i) => (
          <li key={s.key} className="flex items-center gap-1 min-w-0" title={s.at ? `${s.label}: ${fmtWhen(s.at)}` : s.label}>
            <span
              className={`h-4 w-4 flex-shrink-0 rounded-full flex items-center justify-center ${
                s.state === 'done' ? 'bg-green-600 text-white' : s.state === 'current' ? 'bg-primary text-white ring-2 ring-primary/25' : 'bg-white border border-slate-300'
              }`}
            >
              {s.state !== 'todo' && <Icon name="Check" size={10} strokeWidth={3} />}
            </span>
            <span className={`text-[11px] leading-tight truncate ${s.state === 'todo' ? 'text-slate-400' : 'text-slate-700 font-medium'}`}>{s.label}</span>
            {i < steps.length - 1 && <span className={`h-px w-2 sm:w-3 flex-shrink-0 ${s.state === 'done' ? 'bg-green-600' : 'bg-slate-300'}`} />}
          </li>
        ))}
      </ol>
      <p className="mt-2 text-xs text-slate-600">
        <span className={`font-medium ${TONE[MAIL_TEXT[mail.status].tone]}`}>E-mail {MAIL_TEXT[mail.status].label.toLowerCase()}</span>
        {mail.at ? ` ${fmtWhen(mail.at)}` : ''}
        <span className="text-slate-400"> · </span>
        <span className={`font-medium ${TONE[linkText.tone]}`}>{linkText.label}</span>
        {link.state === 'opened' && link.opened_at ? ` ${fmtWhen(link.opened_at)}` : ''}
        {pending && (link.state === 'not_opened' || link.state === 'opened') ? <span className="text-slate-500"> · expires {fmtWhen(link.expires_at)}</span> : null}
        {sent > 1 ? <span className="text-slate-500"> · {sent} e-mails sent, the latest counts</span> : null}
      </p>
    </div>
  );
};

const ItemCard = ({ item }) => {
  const kind = KIND[item.kind];
  const status = STATUS[item.status];
  const when = item.status === 'PENDING' ? `Asked ${fmtWhen(item.requested_at)}` : fmtWhen(item.decided_at);
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`activity-${item.kind.toLowerCase()}-${item.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${kind.chip}`}>
          <Icon name={kind.icon} size={11} />
          {kind.label}
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${status.cls}`}>
          <Icon name={status.icon} size={11} />
          {status.label}
        </span>
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-900 break-words">{item.title}</p>
      <p className="text-xs text-slate-500 break-words">
        {[item.nature_of_business, item.supplier_subcategory].filter(Boolean).join(' · ')}
        {item.vendor_email ? <span className="text-slate-400"> · </span> : null}
        {item.vendor_email}
      </p>
      <p className="mt-2 text-xs text-slate-700 flex flex-wrap items-center gap-x-1.5">
        <span className="font-medium">{outcomeText(item)}</span>
        {when && <span className="text-slate-500">· {when}</span>}
      </p>
      {item.comments && <p className="mt-2 border-l-2 border-slate-300 pl-2.5 text-xs italic text-slate-600 whitespace-pre-line break-words">“{item.comments}”</p>}
      {item.delivery && <DeliveryTrail delivery={item.delivery} pending={item.status === 'PENDING'} />}
    </li>
  );
};

/**
 * Everything about one approver, opened from a combination: who they are, the combinations they are
 * in, and every invite or supplier routed to them or decided by them — and, for someone added by
 * e-mail, whether each e-mail arrived and whether its link was opened.
 */
const ApproverDetailsDrawer = ({ entry, rules, onClose }) => {
  const person = entry.person;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const closeRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await approvalRoutingService.getApproverActivity(activityQuery(person));
      setData(res.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.email, person.userId]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const combos = useMemo(() => combosOfPerson(rules, person), [rules, person]);
  const items = data?.items || [];
  const shown = filterItems(items, filter);
  const counts = { all: items.length, waiting: filterItems(items, 'waiting').length, decided: filterItems(items, 'decided').length };
  const user = entry.approver;
  const name = person.email || user?.full_name || data?.person?.full_name || 'Approver';
  const sub = person.email
    ? 'E-mail only · decides through a link, no VMS login'
    : [user?.email || data?.person?.email, ROLE_TEXT[user?.role || data?.person?.role], user && !user.is_active ? 'Inactive' : null].filter(Boolean).join(' · ');

  return createPortal(
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="approver-details">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <section role="dialog" aria-modal="true" aria-labelledby="approver-details-title" className="relative w-full max-w-xl h-full bg-slate-50 shadow-2xl flex flex-col">
        <header className="bg-white border-b border-slate-200 px-5 sm:px-6 pt-5 pb-4 flex-shrink-0">
          <div className="flex items-start gap-3">
            {person.email ? (
              <span className="h-11 w-11 flex-shrink-0 rounded-full flex items-center justify-center bg-sky-50 text-sky-700 border border-sky-200" aria-hidden="true">
                <Icon name="Mail" size={20} />
              </span>
            ) : (
              <span className="h-11 w-11 flex-shrink-0 rounded-full flex items-center justify-center bg-primary/10 text-primary text-sm font-semibold" aria-hidden="true">
                {initials(user?.full_name, user?.email)}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <h2 id="approver-details-title" className="text-lg font-semibold text-slate-900 break-words leading-snug">{name}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} className="p-2 -mr-2 -mt-1 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="Close approver details">
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Approves</p>
            {combos.length ? (
              <ul className="flex flex-wrap gap-1.5" data-testid="approver-combos">
                {combos.map((c) => (
                  <li key={c.key} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${KIND[c.kind].chip}`}>
                    <Icon name={KIND[c.kind].icon} size={12} />
                    <span className="font-semibold">{KIND[c.kind].short}</span>
                    <span className="opacity-80">· {c.label}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500">Not in any combination now.</p>
            )}
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-slate-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-500 mx-auto" />
              <p className="mt-3 text-sm">Loading…</p>
            </div>
          ) : error ? (
            <div className="m-5 p-4 rounded-lg border border-red-200 bg-red-50 flex items-center gap-3">
              <Icon name="AlertCircle" size={18} className="text-red-600" />
              <span className="text-sm text-red-800 flex-1">{error}</span>
              <Button variant="outline" size="sm" onClick={load}>Retry</Button>
            </div>
          ) : (
            <div className="px-5 sm:px-6 py-5 space-y-4">
              <div className="grid grid-cols-3 gap-2" data-testid="approver-summary">
                {[
                  ['Waiting on them', data.summary.waiting, 'text-amber-700'],
                  ['Approved', data.summary.approved, 'text-green-700'],
                  ['Rejected', data.summary.rejected, 'text-red-700'],
                ].map(([label, n, cls]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                    <p className={`text-xl font-bold leading-none ${n ? cls : 'text-slate-400'}`}>{n}</p>
                    <p className="mt-1 text-[11px] text-slate-500 leading-tight">{label}</p>
                  </div>
                ))}
              </div>

              {items.length > 0 && (
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="Show">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      role="tab"
                      aria-selected={filter === f.key}
                      onClick={() => setFilter(f.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filter === f.key ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                    >
                      {f.label} <span className={filter === f.key ? 'text-white/80' : 'text-slate-400'}>{counts[f.key]}</span>
                    </button>
                  ))}
                </div>
              )}

              {items.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white px-6 py-10 text-center">
                  <Icon name="Inbox" size={28} className="mx-auto text-slate-300" />
                  <p className="mt-3 text-sm text-slate-600">Nothing has been routed to {person.email ? 'this address' : 'them'} yet.</p>
                  <p className="mt-1 text-xs text-slate-500">Requests appear here once an invite or supplier matches one of their combinations.</p>
                </div>
              ) : shown.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">Nothing {filter === 'waiting' ? 'is waiting' : 'decided yet'}.</p>
              ) : (
                <ul className="space-y-3" data-testid="approver-activity">
                  {shown.map((item) => (
                    <ItemCard key={`${item.kind}-${item.id}`} item={item} />
                  ))}
                </ul>
              )}

              {person.email && items.some((i) => i.delivery) && (
                <p className="text-[11px] leading-relaxed text-slate-500">
                  Delivery and opens are reported by the mail service. Some mail apps block open tracking, so an e-mail can be read while it still shows
                  as delivered; opening the link always counts.
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    </div>,
    document.body
  );
};

export default ApproverDetailsDrawer;
